import {
  CreativeDocumentTransform,
  Rectangle,
} from 'content-creation-common/models';
import { TextProperties } from 'content-creation-common/preview';
import {
  isEditingTemplateMode,
  template as getTemplate,
} from '../../../../selectors/ui';

import { isEmpty as isEmptyArray } from '../../../../util/array';
import {
  deepCopy,
  isEmpty as isEmptyObject,
  isEmptyString,
  isNil,
  isNotNil,
  isObject,
} from '../../../../util/object';
import { clamp, getValidatedNumber, isNumeric } from '../../../../util/numbers';

import Layer from '../../../../model/Layer';
import Template from '../../../../model/Template';

import {
  CONTENT_TYPE,
  TEMPLATE_LAYER_TYPES_WITH_BOUNDS,
  VALID_EASING_FUNCTIONS,
} from '../../../../constants';
import {
  DEFAULT_KEYFRAME_NUMBER,
  DEFAULT_KEYFRAME_TRANSFORM,
  VALID_FILTERS,
} from '../../../creative/constants';
import { getLayerByContentId } from '../../../../selectors/element';
import clientCanvasTextMeasurer from '../../../text/util/measurement';


const updateTextLines = (documentModel, state) => {
  const { content } = documentModel;

  for (const contentDocument of content.values()) {
    if (isNil(contentDocument)) continue;

    const { contentId, contentType } = contentDocument;
    if (contentType !== CONTENT_TYPE.TEXT) {
      continue;
    }
    const layer = getLayerByContentId(state, contentId);
    const creativeLayer = Layer.getCreativeLayer(layer);
    const textProperties = new TextProperties(creativeLayer);
    const measuredProperties = clientCanvasTextMeasurer.getTextRenderProperties(
      textProperties,
    );
    const {
      textHeight,
      textLines,
      textWidth,
      renderedFontSize,
    } = measuredProperties;
    contentDocument.naturalHeight = textHeight;
    contentDocument.naturalWidth = textWidth;
    contentDocument.textLines = textLines;
    contentDocument.renderedFontSize = renderedFontSize;
  }
};

const removeLocalUrlsFromMedia = (documentModel) => {
  const { media } = documentModel;

  for (const mediaDocument of media.values()) {
    if (isNil(mediaDocument)) continue;
    mediaDocument.localUrl = undefined;
  }
};

const removeUnusedMedia = (documentModel) => {
  const { content, media } = documentModel;

  const usedMedia = new Map();

  for (const contentDocument of content.values()) {
    if (isNil(contentDocument)) continue;

    const { mediaId } = contentDocument;

    if (isNil(mediaId) || !media.has(mediaId)) continue;
    usedMedia.set(mediaId, media.get(mediaId));
  }
  documentModel.media = usedMedia;
};

const removeUnusedContent = (documentModel) => {
  const { content, elements } = documentModel;

  const usedContent = new Map();

  for (const elementDocument of elements.values()) {
    if (isNil(elementDocument)) continue;

    const { contentId } = elementDocument;

    if (isNil(contentId) || !content.has(contentId)) continue;
    usedContent.set(contentId, content.get(contentId));
  }
  documentModel.content = usedContent;
};

const removeOrphanChildren = (documentModel) => {
  const { elements, rootElementIds } = documentModel;

  const foundElements = new Map();

  for (const elementDocument of elements.values()) {
    if (isNil(elementDocument)) continue;

    const { elementId, groupId } = elementDocument;
    const hasValidParent = isNotNil(groupId) && elements.has(groupId);

    // if element is a root element, we check the followings:
    // a) if it has a valid parent, remove element from root
    // b) else, clean up it's groupId
    if (rootElementIds.has(elementId)) {
      if (hasValidParent) {
        rootElementIds.delete(elementId);
      } else {
        elementDocument.groupId = null;
        elementDocument.parentContentId = null;
      }

      foundElements.set(elementId, elementDocument);
      continue;
    }

    // if element is not in root, make sure its parent exists
    if (hasValidParent) {
      foundElements.set(elementId, elementDocument);
    }
  }

  documentModel.elements = foundElements;
};

const removeEditedTransform = (documentModel) => {
  const { elements } = documentModel;

  for (const elementDocument of elements.values()) {
    if (isNil(elementDocument)) continue;

    elementDocument.isEditingTransform = false;
    elementDocument.editedTransform = undefined;
  }
};

const removeInvalidFilters = (filters) => {
  if (isEmptyObject(filters)) return;
  const { filtersByName, filters: orderOfFilters } = filters;

  if (isEmptyArray(orderOfFilters)) return;

  const updatedFiltersByName = {};
  orderOfFilters.forEach((filterName, index) => {
    if (VALID_FILTERS.has(filterName) && filtersByName[filterName]) {
      updatedFiltersByName[filterName] = filtersByName[filterName];
    } else {
      orderOfFilters.splice(index, 1);
    }

    filters.filtersByName = updatedFiltersByName;
  });
};

const sanitizeTransforms = (documentModel) => {
  const { elements } = documentModel;

  for (const elementDocument of elements.values()) {
    if (isNil(elementDocument)) continue;

    // if no transforms, add default.
    const { transforms } = elementDocument;

    if (isNil(transforms) || transforms.size === 0) {
      elementDocument.transforms = new Map();
      elementDocument.transforms.set(
        DEFAULT_KEYFRAME_NUMBER,
        new CreativeDocumentTransform(),
      );
      continue;
    }

    Array.from(transforms.entries()).forEach(([frame, transform]) => {
      if (!isNumeric(frame) && transforms.has(frame)) {
        transforms.delete(frame);
        return;
      }

      const {
        alpha,
        easingFunction,
        filters,
        rotation,
        scale,
        translation,
      } = transform;

      // Check alpha
      transform.alpha = clamp(
        getValidatedNumber(alpha, DEFAULT_KEYFRAME_TRANSFORM.alpha),
        0,
        1,
      );

      // Check easingFunction
      if (
        isEmptyString(easingFunction) ||
        !VALID_EASING_FUNCTIONS.has(easingFunction.toUpperCase())
      ) {
        transform.easingFunction = DEFAULT_KEYFRAME_TRANSFORM.easingFunction;
      } else {
        transform.easingFunction = easingFunction.toUpperCase();
      }

      // Check rotation
      transform.rotation = getValidatedNumber(
        rotation,
        DEFAULT_KEYFRAME_TRANSFORM.rotation,
      );

      // Check scale
      if (isEmptyObject(scale)) {
        transform.scale = deepCopy(DEFAULT_KEYFRAME_TRANSFORM.scale);
      } else {
        const { x, y } = scale;
        scale.x = getValidatedNumber(x, DEFAULT_KEYFRAME_TRANSFORM.scale.x);
        scale.y = getValidatedNumber(y, DEFAULT_KEYFRAME_TRANSFORM.scale.y);
      }

      // Check translation
      if (isEmptyObject(translation)) {
        transform.translation = deepCopy(
          DEFAULT_KEYFRAME_TRANSFORM.translation,
        );
      } else {
        const { x, y } = translation;
        translation.x = getValidatedNumber(
          x,
          DEFAULT_KEYFRAME_TRANSFORM.translation.x,
        );
        translation.y = getValidatedNumber(
          y,
          DEFAULT_KEYFRAME_TRANSFORM.translation.y,
        );
      }

      if (isEmptyObject(filters) || isEmptyArray(filters.filters)) {
        transform.filters = deepCopy(DEFAULT_KEYFRAME_TRANSFORM.filters);
      } else {
        removeInvalidFilters(filters);
      }
    });

    // Check for emptiness again
    if (isNil(transforms) || transforms.size === 0) {
      elementDocument.transforms = new Map();
      elementDocument.transforms.set(
        DEFAULT_KEYFRAME_NUMBER,
        new CreativeDocumentTransform(),
      );
      continue;
    }
  }
};

const addBoundingBoxes = (documentModel, state) => {
  const editingTemplateMode = isEditingTemplateMode(state);
  const template = getTemplate(state);
  const createdFromTemplate =
    !editingTemplateMode && Template.isTemplate(template);

  // if currently editing template, or editing a creative without template
  // use the current creative json as the original creative

  // TODO: DETERMINE IF "TEXT" IS NECESSARY ON THE MODEL
  const currentCreative = new Template({ data: JSON.stringify(documentModel) });

  // bounds based on template
  const { content, elements, media } = documentModel;

  for (const elementDocument of elements.values()) {
    if (isNil(elementDocument)) continue;

    const { elementId, contentId } = elementDocument;

    if (!isObject(elementDocument.meta)) {
      elementDocument.meta = {};
    }

    if (!content.has(contentId)) {
      elementDocument.meta.bounds = undefined;
      continue;
    }

    const contentDocument = content.get(contentId);

    const { contentType, mediaId } = contentDocument;

    if (!TEMPLATE_LAYER_TYPES_WITH_BOUNDS.has(contentType)) {
      elementDocument.meta.bounds = undefined;
      continue;
    }

    // if creative is using a template, save bounds of the original template layer
    // otherwise calculate bounds of itself
    if (createdFromTemplate) {
      const templateElement = template.getElementByElementId(elementId);
      if (
        isNotNil(templateElement) &&
        isNotNil(templateElement.meta) &&
        isNotNil(templateElement.meta.bounds)
      ) {
        elementDocument.meta.bounds = templateElement.meta.bounds;
        continue;
      }
    }

    // catch all for bound calculations
    const mediumDocument = media.has(mediaId) ? media.get(mediaId) : undefined;
    const layer = Layer.getLayerFromCreativeDocumentObjects(
      contentDocument,
      elementDocument,
      mediumDocument,
    );
    const bounds = Layer.generateBoundingBoxInMediumSpace(
      layer,
      currentCreative,
    );
    if (Rectangle.isValidRectangle(bounds)) {
      elementDocument.meta.bounds = {
        centerX: bounds.x,
        centerY: bounds.y,
        height: bounds.height,
        width: bounds.width,
      };
    }
  }
};

export const sanitizeElements = (documentModel, state) => {
  // remove orphan children layers
  removeOrphanChildren(documentModel);

  // Remove unneeded properties, like editElement;
  removeEditedTransform(documentModel);

  // Ensure all transforms are numbers
  sanitizeTransforms(documentModel);

  // Add calculated bounding boxes for each layer
  addBoundingBoxes(documentModel, state);

  return documentModel;
};

export const sanitizeContent = (documentModel, state) => {
  // Remove all content not referenced.
  removeUnusedContent(documentModel);

  // Make sure all text layers are measured
  updateTextLines(documentModel, state);

  return Promise.resolve(documentModel);
};

export const sanitizeMedia = (documentModel) => {
  // Remove all media not referenced by content.
  removeUnusedMedia(documentModel);

  // Remove any local URLs from media
  removeLocalUrlsFromMedia(documentModel);

  return documentModel;
};
